import React, { useRef, useState } from 'react';
import { adminApi } from '../api';
import { STATUS, INPUT } from '../../styles/tokens';

interface Props {
  currentPrompt: string;
  onClose: () => void;
  onApply: (mode: 'replace' | 'append', prompt: string) => void;
}

const MAX_FILE_BYTES = 10 * 1024 * 1024;
const ALLOWED_MIMES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

interface ImageSlotProps {
  label: string;
  hint: string;
  file: File | null;
  preview: string;
  onPick: (file: File | null) => void;
}

const ImageSlot: React.FC<ImageSlotProps> = ({ label, hint, file, preview, onPick }) => {
  const ref = useRef<HTMLInputElement>(null);
  return (
    <div>
      <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1.5">
        {label} <span className="text-gray-400 dark:text-gray-500 font-normal">{hint}</span>
      </label>
      <input
        ref={ref}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        onChange={e => { onPick(e.target.files?.[0] ?? null); e.target.value = ''; }}
        className="hidden"
      />
      {preview ? (
        <div className="space-y-2">
          <img src={preview} alt={label} className="w-full h-48 object-contain rounded border dark:border-gray-600 bg-gray-50 dark:bg-gray-900" />
          <div className="flex items-center gap-2 text-xs">
            <button onClick={() => ref.current?.click()} className="text-blue-500 hover:text-blue-700">更換</button>
            <button onClick={() => onPick(null)} className="text-red-400 hover:text-red-600">移除</button>
            <span className="text-gray-400 truncate ml-auto">{file?.name}</span>
          </div>
        </div>
      ) : (
        <button
          onClick={() => ref.current?.click()}
          className="w-full border-2 border-dashed dark:border-gray-600 rounded-lg py-12 text-sm text-gray-500 dark:text-gray-400 hover:border-emerald-400 hover:text-emerald-500 transition-colors"
        >
          點此選擇圖片
        </button>
      )}
    </div>
  );
};

const TransferStylePromptDesignerModal: React.FC<Props> = ({ currentPrompt, onClose, onApply }) => {
  const [source, setSource] = useState<File | null>(null);
  const [sourcePreview, setSourcePreview] = useState('');
  const [target, setTarget] = useState<File | null>(null);
  const [targetPreview, setTargetPreview] = useState('');
  const [notes, setNotes] = useState('');
  const [generatedPrompt, setGeneratedPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const validateAndPick = (file: File | null, setter: (f: File | null) => void, previewSetter: (s: string) => void) => {
    if (!file) {
      setter(null);
      previewSetter('');
      return;
    }
    if (!ALLOWED_MIMES.includes(file.type)) return setError('僅支援 JPEG / PNG / WebP / GIF');
    if (file.size > MAX_FILE_BYTES) return setError('檔案大小不能超過 10 MB');
    setError('');
    setter(file);
    previewSetter(URL.createObjectURL(file));
  };

  const handleGenerate = async () => {
    if (!source || !target) return setError('請同時上傳原始圖與目標成品');
    setLoading(true);
    setError('');
    try {
      const { prompt } = await adminApi.suggestTransferStylePrompt({
        source, target, notes: notes.trim() || undefined,
      });
      setGeneratedPrompt(prompt);
    } catch (e: any) {
      setError(e.message || '產生失敗');
    } finally {
      setLoading(false);
    }
  };

  const apply = (mode: 'replace' | 'append') => {
    if (!generatedPrompt.trim()) return;
    onApply(mode, generatedPrompt.trim());
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-3.5 border-b dark:border-gray-700">
          <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100">AI 協助設計 Prompt</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-lg leading-none">✕</button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
            上傳一張「原始圖」與一張「目標成品」，AI 會比對兩張圖在線條、色調、筆觸、構圖等面向的差異，產出一段可重複套用的繁體中文 style-transfer prompt。
          </p>

          <div className="grid grid-cols-2 gap-4">
            <ImageSlot
              label="原始圖"
              hint="（風格轉換前的樣本）"
              file={source}
              preview={sourcePreview}
              onPick={f => validateAndPick(f, setSource, setSourcePreview)}
            />
            <ImageSlot
              label="目標成品"
              hint="（想要的風格參考）"
              file={target}
              preview={targetPreview}
              onPick={f => validateAndPick(f, setTarget, setTargetPreview)}
            />
          </div>

          <div>
            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1.5">補充說明（選填）</label>
            <textarea
              rows={2}
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="例如：請特別注意筆觸、忽略色調差異..."
              className="w-full text-sm border dark:border-gray-600 rounded px-2 py-1.5 bg-white dark:bg-gray-700 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-emerald-400 resize-y"
            />
          </div>

          {error && (
            <p className={`text-sm ${STATUS.error} px-3 py-2 rounded`}>{error}</p>
          )}

          <button
            onClick={handleGenerate}
            disabled={loading || !source || !target}
            className="w-full text-sm bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            {loading ? 'AI 分析中... 約需 10–20 秒' : '分析並產生 Prompt'}
          </button>

          {generatedPrompt && (
            <div className="space-y-2 border-t dark:border-gray-700 pt-4">
              <div className="flex items-center justify-between">
                <label className="text-xs font-medium text-gray-500 dark:text-gray-400">AI 產出 Prompt（可手動編輯）</label>
                <button onClick={() => setGeneratedPrompt('')} className="text-xs text-gray-400 hover:text-gray-600">清除</button>
              </div>
              <textarea
                rows={8}
                value={generatedPrompt}
                onChange={e => setGeneratedPrompt(e.target.value)}
                className={`w-full text-sm font-mono rounded px-2 py-1.5 resize-y leading-relaxed ${INPUT.label}`}
              />
              <div className="flex items-center gap-2">
                <button
                  onClick={() => apply('replace')}
                  className="text-sm bg-emerald-600 text-white px-4 py-1.5 rounded-lg hover:bg-emerald-700 transition-colors"
                >覆蓋現有 Prompt</button>
                <button
                  onClick={() => apply('append')}
                  className="text-sm border border-emerald-600 text-emerald-700 dark:text-emerald-300 px-4 py-1.5 rounded-lg hover:bg-emerald-50 dark:hover:bg-emerald-900/30 transition-colors"
                  disabled={!currentPrompt.trim()}
                  title={!currentPrompt.trim() ? '目前 prompt 為空，請改用覆蓋' : ''}
                >附加到末尾</button>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end px-5 py-3 border-t dark:border-gray-700">
          <button onClick={onClose} className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">關閉</button>
        </div>
      </div>
    </div>
  );
};

export default TransferStylePromptDesignerModal;
